import { useEffect, useMemo, useState } from 'react'
import Alert from 'react-bootstrap/Alert'
import Badge from 'react-bootstrap/Badge'
import Button from 'react-bootstrap/Button'
import Card from 'react-bootstrap/Card'
import Collapse from 'react-bootstrap/Collapse'
import Form from 'react-bootstrap/Form'
import ListGroup from 'react-bootstrap/ListGroup'
import type { GroupBalances, GroupDetail, MemberBalance, Settlement, Transaction } from '../../../../types'
import { useAuth } from '../../auth/AuthContext'
import { useCurrencies } from '../../hooks/useCurrencies'
import { useReceiptQueue } from '../../hooks/useReceipts'
import { formatDate } from '../../utils/format'
import { exponentOf, formatMoney } from '../../utils/money'
import { ZaCoClena, ZaCoPrevodu } from './DebtDetails'
import { PaymentQrModal } from './PaymentQrModal'
import { TransactionFormModal } from './TransactionFormModal'

export type BalancesTabProps = {
    group: GroupDetail
    balances: GroupBalances
    /** Pohyby ze snímku skupiny - pro poslední úhrady a útraty za tento měsíc. */
    transactions: Transaction[]
    /** Offline se smí jen číst. */
    canWrite: boolean
    onChanged: () => void
    /** Přepne na historii - tam čekají účtenky ke kontrole. */
    onShowHistory: () => void
}

/** Kolik posledních úhrad se ukáže. */
const POSLEDNICH_UHRAD = 5

/**
 * Dluhy skupiny z pohledu toho, kdo se dívá.
 *
 * Nahoře je, jak na tom je přihlášený člen, pod tím převody, které skupinu
 * vyrovnají (jeho vlastní první), a teprve pak podrobnosti: bilance všech
 * i s tím, z čeho vznikají, souhrn útrat a poslední úhrady.
 */
export function BalancesTab({ group, balances, transactions, canWrite, onChanged, onShowHistory }: BalancesTabProps) {
    const { data: currencies } = useCurrencies()
    const { user } = useAuth()
    const { data: fronta } = useReceiptQueue(group.id, canWrite)

    const [minimize, setMinimize] = useState(group.simplifyDebts)
    const [prefill, setPrefill] = useState<{ type: 'TRANSFER'; fromMemberId: string; toMemberId: string; amount: number } | undefined>()
    const [qrPlatba, setQrPlatba] = useState<{ fromMemberId: string; toMemberId: string; amount: number } | undefined>()
    // Převod, u kterého je rozbalené „Za co?".
    const [zaCo, setZaCo] = useState<string>()

    // Výchozí stav přepínače je nastavení skupiny; po jeho změně se přenastaví.
    useEffect(() => {
        setMinimize(group.simplifyDebts)
    }, [group.simplifyDebts])

    const exponent = exponentOf(currencies, group.baseCurrencyCode)
    const castka = (hodnota: number) => formatMoney(hodnota, group.baseCurrencyCode, exponent)
    const clen = (memberId: string) => group.members.find((member) => member.id === memberId)
    const jmeno = (memberId: string) => clen(memberId)?.displayName ?? 'Neznámý'
    // QR platbu lze nabídnout jen tomu, kdo má v nastavení vyplněné číslo účtu.
    const maUcet = (memberId: string) => clen(memberId)?.hasBankAccount ?? false

    // Člen skupiny, za kterého je přihlášený uživatel (správce ve skupině být nemusí).
    const ja = group.members.find((member) => member.userId && member.userId === user?.id)
    const mojeBilance = ja ? balances.balances.find((entry) => entry.memberId === ja.id) : undefined

    // Vlastní převody nahoru - ty uživatele zajímají nejvíc.
    const settlements: Settlement[] = useMemo(() => {
        const vsechny = minimize ? balances.minimized : balances.pairwise
        const tyka = (settlement: Settlement) => !!ja && (settlement.fromMemberId === ja.id || settlement.toMemberId === ja.id)
        return [...vsechny.filter(tyka), ...vsechny.filter((settlement) => !tyka(settlement))]
    }, [minimize, balances.minimized, balances.pairwise, ja])

    const vyrovnano = balances.balances.every((entry) => entry.balance === 0)
    const cekaKeKontrole = fronta?.length ?? 0

    const zaznamenat = (settlement: Settlement) =>
        setPrefill({ type: 'TRANSFER', fromMemberId: settlement.fromMemberId, toMemberId: settlement.toMemberId, amount: settlement.amount })

    return (
        <>
            {ja && mojeBilance && (
                <MojeBilance
                    jaId={ja.id}
                    bilance={mojeBilance}
                    settlements={settlements}
                    jmeno={jmeno}
                    castka={castka}
                />
            )}

            {cekaKeKontrole > 0 && (
                <Alert variant="light" className="border py-2 d-flex align-items-center justify-content-between gap-2 flex-wrap" data-testid="dluhy-ke-kontrole">
                    <span className="small">
                        <i className="bi bi-receipt me-1" aria-hidden="true" />
                        {cekaKeKontrole === 1
                            ? '1 účtenka ke kontrole se do dluhů zatím nepočítá.'
                            : `${cekaKeKontrole} ${cekaKeKontrole <= 4 ? 'účtenky' : 'účtenek'} ke kontrole se do dluhů zatím nepočítají.`}
                    </span>
                    <Button variant="outline-secondary" size="sm" className="su-tappable" onClick={onShowHistory}>
                        Zkontrolovat
                    </Button>
                </Alert>
            )}

            <Card className="mb-3">
                <Card.Header className="d-flex align-items-center justify-content-between gap-2 flex-wrap">
                    <span className="fw-semibold">Kdo komu pošle</span>
                    <Form.Check
                        type="switch"
                        id="mene-prevodu"
                        label="Méně převodů"
                        checked={minimize}
                        onChange={(event) => setMinimize(event.target.checked)}
                    />
                </Card.Header>
                <ListGroup variant="flush">
                    {settlements.map((settlement) => {
                        const moje = !!ja && (settlement.fromMemberId === ja.id || settlement.toMemberId === ja.id)
                        const klic = `${settlement.fromMemberId}-${settlement.toMemberId}`
                        return (
                            <ListGroup.Item
                                key={klic}
                                className="d-flex align-items-center gap-2 flex-wrap"
                                // Vlastní převod označí proužek vlevo - rámeček by ho slil se sousedními řádky.
                                style={moje ? { boxShadow: 'inset 4px 0 0 var(--bs-primary)' } : undefined}
                            >
                                <div className="flex-grow-1 min-w-0">
                                    <span className="fw-semibold">{jmeno(settlement.fromMemberId)}</span>
                                    {settlement.fromMemberId === ja?.id && <Badge bg="primary" className="ms-1">ty</Badge>}
                                    <i className="bi bi-arrow-right mx-2 text-secondary" aria-hidden="true" />
                                    <span className="fw-semibold">{jmeno(settlement.toMemberId)}</span>
                                    {settlement.toMemberId === ja?.id && <Badge bg="primary" className="ms-1">ty</Badge>}
                                </div>
                                <span className="fw-semibold">{castka(settlement.amount)}</span>
                                {maUcet(settlement.toMemberId) && (
                                    <Button
                                        size="sm"
                                        variant="outline-secondary"
                                        className="su-tappable"
                                        /* QR kód sestavuje server, offline ho nabídnout nejde. */
                                        disabled={!canWrite}
                                        title={`Zaplatit QR kódem: ${jmeno(settlement.toMemberId)}`}
                                        aria-label={`Zaplatit QR kódem: ${jmeno(settlement.toMemberId)}`}
                                        onClick={() => setQrPlatba(settlement)}
                                    >
                                        <i className="bi bi-qr-code" aria-hidden="true" />
                                    </Button>
                                )}
                                <Button size="sm" variant="outline-primary" className="su-tappable" disabled={!canWrite} onClick={() => zaznamenat(settlement)}>
                                    Zaznamenat úhradu
                                </Button>
                                {/* Zjednodušený převod nevznikl z konkrétních útrat - rozepsat jde jen párový. */}
                                {!minimize && canWrite && (
                                    <div className="w-100">
                                        <Button
                                            variant="link"
                                            size="sm"
                                            className="p-0"
                                            aria-expanded={zaCo === klic}
                                            onClick={() => setZaCo(zaCo === klic ? undefined : klic)}
                                        >
                                            {zaCo === klic ? 'Skrýt' : 'Za co?'}
                                        </Button>
                                        {zaCo === klic && (
                                            <ZaCoPrevodu
                                                groupId={group.id}
                                                revision={balances.revision}
                                                fromMemberId={settlement.fromMemberId}
                                                toMemberId={settlement.toMemberId}
                                                castka={castka}
                                                jmeno={jmeno}
                                            />
                                        )}
                                    </div>
                                )}
                            </ListGroup.Item>
                        )
                    })}
                    {settlements.length === 0 && (
                        <ListGroup.Item>
                            <div className="text-secondary">Není co vyrovnávat.</div>
                            {vyrovnano && balances.balances.length > 0 && (
                                <div className="text-success small mt-1">
                                    <i className="bi bi-check-circle me-1" aria-hidden="true" />
                                    Všechno je vyrovnané, nikdo nikomu nic nedluží.
                                </div>
                            )}
                        </ListGroup.Item>
                    )}
                </ListGroup>
                <Card.Footer className="small text-secondary">
                    {minimize
                        ? 'Méně převodů: peníze nemusí jít přímo tomu, komu jste dlužili za konkrétní útratu, ale všichni se vyrovnají co nejmenším počtem plateb.'
                        : 'Každý posílá přímo tomu, kdo za něj platil. Zapnutím „Méně převodů“ se platby zjednoduší.'}
                </Card.Footer>
            </Card>

            <PrehledClenu
                group={group}
                revision={balances.revision}
                canWrite={canWrite}
                balances={balances.balances}
                jaId={ja?.id}
                // Kdo má svou kartu nahoře, podrobnosti ostatních nepotřebuje hned.
                rozbaleno={!ja}
                jmeno={jmeno}
                castka={castka}
            />

            <SouhrnSkupiny group={group} balances={balances} transactions={transactions} jmeno={jmeno} castka={castka} />

            <PosledniUhrady transactions={transactions} jaId={ja?.id} jmeno={jmeno} castka={castka} />

            {qrPlatba && (
                <PaymentQrModal
                    show
                    group={group}
                    toMemberId={qrPlatba.toMemberId}
                    fromMemberId={qrPlatba.fromMemberId}
                    amount={qrPlatba.amount}
                    onClose={() => setQrPlatba(undefined)}
                    onRecordPayment={() => {
                        setPrefill({ type: 'TRANSFER', ...qrPlatba })
                        setQrPlatba(undefined)
                    }}
                />
            )}

            {prefill && (
                <TransactionFormModal show group={group} prefill={prefill} onClose={() => setPrefill(undefined)} onSaved={onChanged} />
            )}
        </>
    )
}

type MojeBilanceProps = {
    jaId: string
    bilance: MemberBalance
    settlements: Settlement[]
    jmeno: (memberId: string) => string
    castka: (hodnota: number) => string
}

/** Jak je na tom přihlášený člen - jednou větou a s tím, komu co poslat. */
function MojeBilance({ jaId, bilance, settlements, jmeno, castka }: MojeBilanceProps) {
    const posilam = settlements.filter((settlement) => settlement.fromMemberId === jaId)
    const dostanu = settlements.filter((settlement) => settlement.toMemberId === jaId)

    const [nadpis, barva] =
        bilance.balance < 0
            ? [`Dlužíš ${castka(-bilance.balance)}`, 'text-danger']
            : bilance.balance > 0
              ? [`Dostaneš zpět ${castka(bilance.balance)}`, 'text-success']
              : ['Máš vyrovnáno', 'text-success']

    return (
        <Card className="mb-3" data-testid="moje-bilance">
            <Card.Body>
                <div className="small text-secondary">Ty ve skupině</div>
                <div className={`h3 mb-2 ${barva}`}>
                    {bilance.balance === 0 && <i className="bi bi-check-circle me-2" aria-hidden="true" />}
                    {nadpis}
                </div>
                {posilam.map((settlement) => (
                    <div key={settlement.toMemberId}>
                        Pošli <strong>{jmeno(settlement.toMemberId)}</strong> {castka(settlement.amount)}
                    </div>
                ))}
                {dostanu.map((settlement) => (
                    <div key={settlement.fromMemberId}>
                        <strong>{jmeno(settlement.fromMemberId)}</strong> ti pošle {castka(settlement.amount)}
                    </div>
                ))}
                {(posilam.length > 0 || dostanu.length > 0) && (
                    <div className="small text-secondary mt-2">Platbu zaplatíš nebo zaznamenáš u převodu níže — tvoje jsou označené.</div>
                )}
            </Card.Body>
        </Card>
    )
}

/** Bilance jedním slovem místo znaménka. */
function popisBilance(balance: number, castka: (hodnota: number) => string): { text: string; barva: string } {
    if (balance > 0) {
        return { text: `dostane zpět ${castka(balance)}`, barva: 'text-success' }
    }
    if (balance < 0) {
        return { text: `má doplatit ${castka(-balance)}`, barva: 'text-danger' }
    }
    return { text: 'vyrovnáno', barva: 'text-secondary' }
}

type PrehledClenuProps = {
    group: GroupDetail
    revision: number
    canWrite: boolean
    balances: MemberBalance[]
    jaId?: string
    rozbaleno: boolean
    jmeno: (memberId: string) => string
    castka: (hodnota: number) => string
}

/**
 * Bilance všech členů. Klepnutím na člena se ukáže, z čeho jeho bilance
 * vzniká. Pruh od středu (doleva dluží, doprava dostane) dává smysl až od
 * tří lidí - u dvou je zrcadlový a nic neříká.
 */
function PrehledClenu({ group, revision, canWrite, balances, jaId, rozbaleno, jmeno, castka }: PrehledClenuProps) {
    const [otevreno, setOtevreno] = useState(rozbaleno)
    const [rozpad, setRozpad] = useState<string>()
    const maxBilance = Math.max(1, ...balances.map((entry) => Math.abs(entry.balance)))
    const pruhy = balances.length >= 3
    const barvaClena = (memberId: string) => group.members.find((member) => member.id === memberId)?.color ?? 'var(--bs-secondary)'

    return (
        <Card className="mb-3">
            <Card.Header className="p-0">
                <button
                    type="button"
                    className="btn w-100 text-start d-flex justify-content-between align-items-center su-tappable"
                    aria-expanded={otevreno}
                    aria-controls="prehled-clenu"
                    onClick={() => setOtevreno((soucasne) => !soucasne)}
                >
                    <span className="fw-semibold">Přehled členů</span>
                    <i className={`bi ${otevreno ? 'bi-chevron-up' : 'bi-chevron-down'}`} aria-hidden="true" />
                </button>
            </Card.Header>
            <Collapse in={otevreno}>
                <div id="prehled-clenu">
                    <ListGroup variant="flush" data-testid="prehled-clenu">
                        {balances.map((entry) => {
                            const { text, barva } = popisBilance(entry.balance, castka)
                            const podil = (Math.abs(entry.balance) / maxBilance) * 50
                            const otevrenyRozpad = rozpad === entry.memberId
                            return (
                                <ListGroup.Item key={entry.memberId} className="p-0">
                                    <button
                                        type="button"
                                        className="btn w-100 text-start px-3 py-2 border-0 rounded-0"
                                        aria-expanded={otevrenyRozpad}
                                        onClick={() => setRozpad(otevrenyRozpad ? undefined : entry.memberId)}
                                    >
                                        <div className="d-flex align-items-center gap-2">
                                            <span
                                                className="rounded-circle flex-shrink-0"
                                                style={{ width: '0.9rem', height: '0.9rem', backgroundColor: barvaClena(entry.memberId) }}
                                                aria-hidden="true"
                                            />
                                            <span className="flex-grow-1 text-truncate">
                                                {jmeno(entry.memberId)}
                                                {entry.memberId === jaId && <span className="text-secondary"> (ty)</span>}
                                            </span>
                                            <span className={`fw-semibold ${barva}`}>{text}</span>
                                            <i className={`bi ${otevrenyRozpad ? 'bi-chevron-up' : 'bi-chevron-down'} text-secondary small`} aria-hidden="true" />
                                        </div>
                                        {pruhy && entry.balance !== 0 && (
                                            <div className="position-relative mt-1 rounded bg-body-secondary" style={{ height: '0.35rem' }} aria-hidden="true">
                                                <div className="position-absolute top-0 bottom-0 border-start border-secondary" style={{ left: '50%' }} />
                                                <div
                                                    className={`position-absolute top-0 bottom-0 rounded ${entry.balance > 0 ? 'bg-success' : 'bg-danger'}`}
                                                    style={entry.balance > 0 ? { left: '50%', width: `${podil}%` } : { right: '50%', width: `${podil}%` }}
                                                />
                                            </div>
                                        )}
                                    </button>
                                    <Collapse in={otevrenyRozpad}>
                                        <div>
                                            <RozpadBilance entry={entry} castka={castka} />
                                            {/* Pohyby se načítají ze serveru, offline nejsou k dispozici. */}
                                            {otevrenyRozpad && canWrite && (
                                                <div className="px-3 pb-2">
                                                    <ZaCoClena groupId={group.id} revision={revision} memberId={entry.memberId} castka={castka} jmeno={jmeno} />
                                                </div>
                                            )}
                                        </div>
                                    </Collapse>
                                </ListGroup.Item>
                            )
                        })}
                        {balances.length === 0 && <ListGroup.Item className="text-secondary">Skupina zatím nemá členy.</ListGroup.Item>}
                    </ListGroup>
                </div>
            </Collapse>
        </Card>
    )
}

/** Z čeho bilance člena vzniká. */
function RozpadBilance({ entry, castka }: { entry: MemberBalance; castka: (hodnota: number) => string }) {
    const radky: Array<[string, number, string]> = [
        ['Zaplatil za útraty', entry.paid, '+'],
        ['Jeho útraty', entry.spent, '−'],
        ['Poslal na úhradu dluhů', entry.transfersSent, '+'],
        ['Dostal na úhradu dluhů', entry.transfersReceived, '−'],
    ]
    return (
        <div className="px-3 pb-2 small" data-testid="rozpad-bilance">
            {radky
                .filter(([, hodnota], index) => index < 2 || hodnota !== 0)
                .map(([popis, hodnota, znamenko]) => (
                    <div key={popis} className="d-flex justify-content-between text-secondary">
                        <span>{popis}</span>
                        <span>
                            {znamenko} {castka(hodnota)}
                        </span>
                    </div>
                ))}
            <div className="d-flex justify-content-between fw-semibold border-top mt-1 pt-1">
                <span>Bilance</span>
                <span>{castka(entry.balance)}</span>
            </div>
        </div>
    )
}

type SouhrnSkupinyProps = {
    group: GroupDetail
    balances: GroupBalances
    transactions: Transaction[]
    jmeno: (memberId: string) => string
    castka: (hodnota: number) => string
}

/** Kolik skupina utratila a kdo to platil. */
function SouhrnSkupiny({ group, balances, transactions, jmeno, castka }: SouhrnSkupinyProps) {
    if (balances.totalExpenses === 0) {
        return null
    }

    const ted = new Date()
    const tentoMesic = transactions
        .filter((transaction) => {
            const kdy = new Date(transaction.occurredAt)
            return transaction.type === 'EXPENSE' && kdy.getFullYear() === ted.getFullYear() && kdy.getMonth() === ted.getMonth()
        })
        .reduce((soucet, transaction) => soucet + transaction.amountTotalBase, 0)
    const platici = balances.balances.filter((entry) => entry.paid > 0).sort((a, b) => b.paid - a.paid)
    const barvaClena = (memberId: string) => group.members.find((member) => member.id === memberId)?.color ?? 'var(--bs-secondary)'

    return (
        <Card className="mb-3" data-testid="souhrn-skupiny">
            <Card.Header className="fw-semibold">Souhrn skupiny</Card.Header>
            <Card.Body>
                <div className="d-flex gap-4 flex-wrap mb-3">
                    <div>
                        <div className="small text-secondary">Celkem utraceno</div>
                        <div className="h5 mb-0">{castka(balances.totalExpenses)}</div>
                    </div>
                    <div>
                        <div className="small text-secondary">Tento měsíc</div>
                        <div className="h5 mb-0">{castka(tentoMesic)}</div>
                    </div>
                </div>
                <div className="small text-secondary mb-1">Kdo kolik zaplatil</div>
                {platici.map((entry) => (
                    <div key={entry.memberId} className="mb-2">
                        <div className="d-flex justify-content-between small">
                            <span>{jmeno(entry.memberId)}</span>
                            <span>{castka(entry.paid)}</span>
                        </div>
                        <div className="rounded bg-body-secondary" style={{ height: '0.35rem' }} aria-hidden="true">
                            <div
                                className="rounded h-100"
                                style={{ width: `${(entry.paid / balances.totalExpenses) * 100}%`, backgroundColor: barvaClena(entry.memberId) }}
                            />
                        </div>
                    </div>
                ))}
            </Card.Body>
        </Card>
    )
}

type PosledniUhradyProps = {
    transactions: Transaction[]
    jaId?: string
    jmeno: (memberId: string) => string
    castka: (hodnota: number) => string
}

/** Kdo komu naposledy poslal - pro kontrolu, že úhrada je zapsaná. */
function PosledniUhrady({ transactions, jaId, jmeno, castka }: PosledniUhradyProps) {
    const uhrady = transactions
        .filter((transaction) => transaction.type === 'TRANSFER')
        .sort((a, b) => b.occurredAt.localeCompare(a.occurredAt))
        .slice(0, POSLEDNICH_UHRAD)

    if (uhrady.length === 0) {
        return null
    }

    const sTy = (memberId: string | undefined) => (memberId ? `${jmeno(memberId)}${memberId === jaId ? ' (ty)' : ''}` : 'Neznámý')

    return (
        <Card className="mb-3" data-testid="posledni-uhrady">
            <Card.Header className="fw-semibold">Poslední úhrady</Card.Header>
            <ListGroup variant="flush">
                {uhrady.map((transaction) => (
                    <ListGroup.Item key={transaction.id} className="d-flex align-items-center gap-2 small">
                        <span className="flex-grow-1 min-w-0 text-truncate">
                            {sTy(transaction.payers[0]?.memberId)}
                            <i className="bi bi-arrow-right mx-2 text-secondary" aria-hidden="true" />
                            {sTy(transaction.shares[0]?.memberId)}
                        </span>
                        <span className="text-secondary">{formatDate(transaction.occurredAt)}</span>
                        <span className="fw-semibold">{castka(transaction.amountTotalBase)}</span>
                    </ListGroup.Item>
                ))}
            </ListGroup>
        </Card>
    )
}
